import { useCallback, useState } from 'react'
import { Upload, FileText, AlertCircle } from 'lucide-react'

const FileUpload = ({ onFileUpload, loading, error, processingProgress, uploadResults = [], s3Enabled = false }) => {
  const [dragActive, setDragActive] = useState(false)
  
  // Debug logging (disabled in production)
  if (import.meta.env.DEV) {
    console.log('FileUpload props:', {
      s3Enabled,
      uploadResults,
      loading,
      error,
      processingProgress
    })
  }

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files)
      const csvFiles = files.filter(file => 
        file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
      )
      
      if (csvFiles.length === 0) {
        alert('Please drop CSV files')
      } else if (csvFiles.length === 1) {
        onFileUpload(csvFiles[0])
      } else {
        onFileUpload(csvFiles)
      }
    }
  }, [onFileUpload])

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files)
      const csvFiles = files.filter(file => 
        file.name.toLowerCase().endsWith('.csv')
      )
      
      if (csvFiles.length === 0) {
        alert('Please select CSV files')
        return
      } else if (csvFiles.length === 1) {
        onFileUpload(csvFiles[0])
      } else {
        onFileUpload(csvFiles)
      }
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Drone Methane Detection Viewer
        </h2>
        <p className="text-lg text-gray-600">
          Upload your drone CSV data to visualize methane measurements and detect potential leaks
        </p>
      </div>

      <div
        className={`upload-zone ${dragActive ? 'dragover' : ''} ${loading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-lg text-gray-600 mb-2">
                {processingProgress?.stage || 'Processing CSV files...'}
              </p>
              {processingProgress?.currentFile && (
                <p className="text-sm text-gray-500 mb-2">
                  File: {processingProgress.currentFile}
                </p>
              )}
              {processingProgress?.total > 0 && (
                <div className="w-full max-w-xs">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>File {processingProgress.current} of {processingProgress.total}</span>
                    <span>{Math.round((processingProgress.current / processingProgress.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(processingProgress.current / processingProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
              {/* Upload Progress */}
              {processingProgress?.stage === 'upload' && (
                <div className="w-full max-w-xs mt-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{processingProgress.uploadProgress?.stage || 'Uploading to S3...'}</span>
                    <span>{processingProgress.uploadProgress?.percentage || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${processingProgress.uploadProgress?.percentage || 0}%` }}
                    ></div>
                  </div>
                  {processingProgress.uploadProgress?.currentFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      {processingProgress.uploadProgress.currentFile}
                    </p>
                  )}
                </div>
              )}
              
              {/* Data Processing Progress */}
              {processingProgress?.dataProgress && (
                <div className="w-full max-w-xs mt-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{processingProgress.dataProgress.stage || 'Processing data...'}</span>
                    <span>{processingProgress.dataProgress.percentage || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${processingProgress.dataProgress.percentage || 0}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <Upload className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg text-gray-600 mb-2">
                Drag and drop your CSV files here, or click to browse
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Supports single or multiple files from drone methane detection systems
              </p>
              <input
                type="file"
                accept=".csv"
                multiple
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
              >
                Choose Files
              </label>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            S3 Upload Results {s3Enabled ? '✅' : '⚠️'}
          </h3>
          <div className="space-y-2">
            {uploadResults.map((result, index) => (
              <div key={index} className="text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="font-medium text-blue-700">{result.fileName}</span>
                    {result.newFileName && result.newFileName !== result.fileName && (
                      <span className="text-xs text-blue-500">→ {result.newFileName}</span>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    result.uploaded 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {result.uploaded ? 'Uploaded' : 'Skipped'}
                  </span>
                </div>
                <p className="text-blue-600 text-xs mt-1">{result.reason}</p>
                {result.url && (
                  <a 
                    href={result.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 text-xs underline"
                  >
                    View in S3
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* S3 Configuration Warning */}
      {!s3Enabled && !loading && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <p className="text-blue-700 text-sm font-medium">Development Mode</p>
              <p className="text-blue-600 text-xs mt-1">
                Files are processed locally only. S3 upload is automatically enabled in production.
              </p>
              <p className="text-blue-500 text-xs mt-1">
                Current hostname: {window.location.hostname}
              </p>
            </div>
          </div>
        </div>
      )}


      {/* CORS Error Warning */}
      {error && error.includes('CORS error') && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <p className="text-red-700 text-sm font-medium">CORS Configuration Required</p>
              <p className="text-red-600 text-xs mt-1">
                Your DigitalOcean Spaces bucket needs CORS configuration to allow uploads from this domain.
              </p>
              <p className="text-red-600 text-xs mt-1">
                Please follow the instructions in <code className="bg-red-100 px-1 rounded">docs/CORS_SETUP.md</code>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 text-sm text-gray-600">
        <h3 className="font-semibold mb-3 text-gray-800">Data Quality Assessment:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 p-3 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">✅ Good Quality Data</h4>
            <ul className="space-y-1 text-green-700">
              <li>• FALCON_STATE = 1 (active measurement)</li>
                <li>• FALCON_1F &gt; 30 (sufficient optical intensity)</li>
              <li>• GPS coordinates and altitude available</li>
            </ul>
          </div>
          
          <div className="bg-red-50 p-3 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">🚨 Potential Leak Detection</h4>
            <ul className="space-y-1 text-red-700">
                <li>• FALCON_MEAS &gt; 500 ppm·m</li>
                <li>• FALCON_STATE = 0 or 1 (valid state)</li>
                <li>• FALCON_1F &gt; 30 (good signal quality)</li>
              <li>• Clustered within 30m radius</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-3 rounded-lg">
            <h4 className="font-medium text-yellow-800 mb-2">⚠️ Poor Quality Data</h4>
            <ul className="space-y-1 text-yellow-700">
              <li>• FALCON_STATE ≠ 1 (inactive/error state)</li>
              <li>• FALCON_1F ≤ 30 (low optical intensity)</li>
              <li>• Missing GPS or measurement data</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">⚫ Off/No Data (Gray Points)</h4>
            <ul className="space-y-1 text-gray-700">
              <li>• FALCON_STATE = 0 (system off/inactive)</li>
              <li>• No measurement data available</li>
              <li>• System in calibration or maintenance mode</li>
              <li>• Shows flight path without measurements</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">📊 Leak Severity Levels</h4>
            <ul className="space-y-1 text-blue-700">
                <li>• <strong>Critical:</strong> &gt; 2000 ppm·m</li>
              <li>• <strong>High:</strong> 1000-2000 ppm·m</li>
              <li>• <strong>Medium:</strong> 500-1000 ppm·m</li>
              <li>• <strong>Low:</strong> &lt; 500 ppm·m</li>
            </ul>
          </div>
        </div>
        
        {/* Color Coding Legend */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-3">🎨 Map Color Coding:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="flex items-center">
              <span className="w-4 h-4 rounded-full bg-green-500 mr-2"></span>
              <span className="text-gray-700">Good Quality</span>
            </div>
            <div className="flex items-center">
              <span className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></span>
              <span className="text-gray-700">Poor Quality</span>
            </div>
            <div className="flex items-center">
              <span className="w-4 h-4 rounded-full bg-gray-500 mr-2"></span>
              <span className="text-gray-700">Off/No Data</span>
            </div>
            <div className="flex items-center">
              <span className="w-4 h-4 rounded-full bg-red-500 mr-2"></span>
              <span className="text-gray-700">Potential Leak</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Notice */}
      <div className="mt-12 pt-6 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          © 2024 Uavonic s.r.o. All rights reserved.
        </p>
      </div>
    </div>
  )
}

export default FileUpload
